import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  OfferProposerRole,
  parseUsdcToBaseUnits,
  InvalidAmountError,
  type OfferResponse,
} from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { describeError } from "./States";

/** Accept/counter/decline controls for one offer, shown from either side of the negotiation.
 *  `viewerRole` is which party the current viewer is on *this* offer — not the same thing as
 *  their account-wide role, since the same person can be a buyer on one offer and a seller on
 *  another. */
export function OfferActions({
  offer,
  viewerRole,
}: {
  offer: OfferResponse;
  viewerRole: OfferProposerRole;
}) {
  const queryClient = useQueryClient();
  const [showCounter, setShowCounter] = useState(false);
  const [counterInput, setCounterInput] = useState("");
  const [counterError, setCounterError] = useState<string | null>(null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.myOffers });
    void queryClient.invalidateQueries({ queryKey: queryKeys.sellerOffers });
    void queryClient.invalidateQueries({ queryKey: queryKeys.offer(offer.id) });
  }

  const accept = useMutation({
    mutationFn: () => apiClient.acceptOffer(offer.id),
    onSuccess: invalidate,
  });
  const decline = useMutation({
    mutationFn: () => apiClient.declineOffer(offer.id),
    onSuccess: invalidate,
  });
  const counter = useMutation({
    mutationFn: (amountBaseUnits: string) => apiClient.counterOffer(offer.id, { amountBaseUnits }),
    onSuccess: () => {
      setShowCounter(false);
      setCounterInput("");
      invalidate();
    },
  });

  function handleCounterSubmit(e: FormEvent) {
    e.preventDefault();
    setCounterError(null);
    let amount: bigint;
    try {
      amount = parseUsdcToBaseUnits(counterInput.trim());
    } catch (err) {
      setCounterError(err instanceof InvalidAmountError ? err.message : "Enter a valid USDC amount.");
      return;
    }
    counter.mutate(amount.toString());
  }

  const isMyTurn =
    (offer.status === "PENDING" || offer.status === "COUNTERED") &&
    offer.currentProposerRole !== viewerRole;

  if (offer.status === "ACCEPTED" && offer.canCheckout && viewerRole === OfferProposerRole.BUYER) {
    return (
      <Link
        to={`/checkout/${offer.listingId}?offerId=${offer.id}`}
        className="btn btn-primary btn-sm"
      >
        Check out at negotiated price
      </Link>
    );
  }

  if (!isMyTurn) {
    return <span className="text-sm muted">Waiting on the other party</span>;
  }

  return (
    <div className="stack" style={{ gap: "var(--space-2)" }}>
      <div className="row">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={accept.isPending}
          onClick={() => accept.mutate()}
        >
          Accept
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={showCounter}
          onClick={() => setShowCounter(true)}
        >
          Counter
        </button>
        <button
          type="button"
          className="btn btn-danger btn-sm"
          disabled={decline.isPending}
          onClick={() => decline.mutate()}
        >
          Decline
        </button>
      </div>
      {showCounter && (
        <form className="row" onSubmit={handleCounterSubmit}>
          <input
            type="text"
            inputMode="decimal"
            placeholder="New amount (USDC)"
            value={counterInput}
            onChange={(e) => setCounterInput(e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={counter.isPending}>
            {counter.isPending ? "Sending…" : "Send counter"}
          </button>
        </form>
      )}
      {counterError && (
        <p className="field-error-text" role="alert">
          {counterError}
        </p>
      )}
      {(accept.isError || decline.isError || counter.isError) && (
        <p className="field-error-text" role="alert">
          {describeError(accept.error ?? decline.error ?? counter.error)}
        </p>
      )}
    </div>
  );
}
